
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { WatchedItem } from '../types';
import { Trash2, ThumbsUp, ThumbsDown, History, X, Search, Plus, Loader2, Tv, Film, Check } from 'lucide-react';

export interface ArchiveSearchResult {
  id: string;
  title: string;
  year: number | null;
  posterUrl: string | null;
  contentType: 'movie' | 'tv';
  overview: string;
}

interface HistoryManagerProps {
  history: WatchedItem[];
  onRemove: (title: string) => void;
  onClose: () => void;
  onAddFromSearch: (result: ArchiveSearchResult, liked: boolean) => Promise<void>;
}

const HistoryManager: React.FC<HistoryManagerProps> = ({ history, onRemove, onClose, onAddFromSearch }) => {
  const [showSearch, setShowSearch] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ArchiveSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Tracks which result is currently being saved (so we can disable its buttons + show a spinner)
  const [pendingId, setPendingId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Quick O(1) lookup for "is this title already in the archives?"
  const historyTitles = new Set(history.map(h => h.title.toLowerCase()));

  // Debounced search
  useEffect(() => {
    if (!showSearch) return;

    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setError(null);
      return;
    }

    setIsSearching(true);
    setError(null);
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const response = await fetch(`/api/search?query=${encodeURIComponent(trimmed)}`, {
          signal: controller.signal
        });
        if (!response.ok) throw new Error('Search failed');
        const data = await response.json();
        setResults(data.results || []);
      } catch (err: any) {
        if (err.name !== 'AbortError') {
          console.error('Archive search error:', err);
          setError('The Critic could not reach the archives. Try again shortly.');
          setResults([]);
        }
      } finally {
        setIsSearching(false);
      }
    }, 350);

    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [query, showSearch]);

  // Auto-focus the input when the search panel opens
  useEffect(() => {
    if (showSearch) {
      // Small delay so the animation doesn't steal focus visually
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [showSearch]);

  const handleRate = async (result: ArchiveSearchResult, liked: boolean) => {
    if (historyTitles.has(result.title.toLowerCase())) return;
    setPendingId(result.id);
    try {
      await onAddFromSearch(result, liked);
      // Clear the query after a successful add so the user can search for the next one
      setQuery('');
      setResults([]);
    } finally {
      setPendingId(null);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/95 backdrop-blur-2xl"
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        className="bg-[#3a0303] max-w-4xl w-full h-[80vh] flex flex-col relative rounded-xl border-4 border-[#d4af37] shadow-[0_0_50px_rgba(212,175,55,0.3)]"
      >
        <div className="p-8 border-b border-[#d4af37]/20 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <History className="text-[#d4af37]" />
            <h2 className="font-marquee text-3xl text-[#d4af37]">The Archives</h2>
          </div>
          <button onClick={onClose} aria-label="Close History" className="text-[#d4af37] hover:text-white transition-colors">
            <X size={32} />
          </button>
        </div>

        {/* "Add a Title" search section */}
        <div className="border-b border-[#d4af37]/20 bg-black/20">
          <button
            onClick={() => setShowSearch(s => !s)}
            className="w-full px-8 py-4 flex items-center justify-between text-[#d4af37] hover:bg-[#d4af37]/5 transition-colors"
            aria-expanded={showSearch}
          >
            <div className="flex items-center gap-3">
              <Plus size={18} className={`transition-transform ${showSearch ? 'rotate-45' : ''}`} />
              <span className="text-[10px] font-bold uppercase tracking-[0.2em]">Add a Title to Your Archives</span>
            </div>
            <span className="text-[10px] text-[#d4af37]/50 uppercase tracking-widest hidden md:inline">
              {showSearch ? 'Close' : 'Open'}
            </span>
          </button>

          <AnimatePresence initial={false}>
            {showSearch && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="overflow-hidden"
              >
                <div className="px-8 pb-6 space-y-4">
                  {/* Search input */}
                  <div className="relative">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#d4af37]/60" />
                    <input
                      ref={inputRef}
                      type="text"
                      value={query}
                      onChange={e => setQuery(e.target.value)}
                      placeholder="Search for a movie or TV show..."
                      className="w-full bg-black/40 border border-[#d4af37]/30 focus:border-[#d4af37] text-white placeholder:text-white/30 text-sm pl-10 pr-10 py-3 rounded-sm outline-none transition-colors"
                    />
                    {isSearching && (
                      <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#d4af37] animate-spin" />
                    )}
                    {!isSearching && query && (
                      <button
                        onClick={() => setQuery('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#d4af37]/60 hover:text-[#d4af37]"
                        aria-label="Clear search"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>

                  {/* Error state */}
                  {error && (
                    <p className="text-xs text-red-400 font-serif-elegant italic">{error}</p>
                  )}

                  {/* Results */}
                  {results.length > 0 && (
                    <div className="max-h-[30vh] overflow-y-auto space-y-2 pr-1">
                      {results.map(result => {
                        const alreadyAdded = historyTitles.has(result.title.toLowerCase());
                        const isPending = pendingId === result.id;
                        return (
                          <motion.div
                            key={result.id}
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="flex items-center gap-3 p-3 bg-black/30 border border-[#d4af37]/10 rounded-lg hover:border-[#d4af37]/30 transition-all"
                          >
                            {/* Poster thumbnail */}
                            <div className="flex-shrink-0 w-12 h-16 bg-[#1a0101] rounded-sm overflow-hidden border border-[#d4af37]/20">
                              {result.posterUrl ? (
                                <img
                                  src={result.posterUrl}
                                  alt={result.title}
                                  className="w-full h-full object-cover"
                                  onError={e => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-[#d4af37]/30">
                                  {result.contentType === 'tv' ? <Tv size={16} /> : <Film size={16} />}
                                </div>
                              )}
                            </div>

                            {/* Title + meta */}
                            <div className="flex-1 min-w-0">
                              <h4 className="font-bold text-white text-sm truncate">{result.title}</h4>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-[9px] text-[#d4af37]/70 uppercase tracking-widest flex items-center gap-1">
                                  {result.contentType === 'tv' ? <Tv size={9} /> : <Film size={9} />}
                                  {result.contentType === 'tv' ? 'TV' : 'Film'}
                                </span>
                                {result.year && (
                                  <span className="text-[9px] text-white/40 uppercase tracking-widest">{result.year}</span>
                                )}
                              </div>
                            </div>

                            {/* Actions */}
                            <div className="flex-shrink-0">
                              {alreadyAdded ? (
                                <div className="flex items-center gap-1.5 text-[9px] text-[#d4af37]/60 uppercase tracking-widest px-2 py-1 border border-[#d4af37]/20 rounded-sm">
                                  <Check size={10} /> In Archives
                                </div>
                              ) : isPending ? (
                                <div className="flex items-center justify-center w-[120px] py-1.5">
                                  <Loader2 size={14} className="text-[#d4af37] animate-spin" />
                                </div>
                              ) : (
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleRate(result, true)}
                                    aria-label={`Mark ${result.title} as loved`}
                                    className="flex items-center gap-1 border border-[#d4af37]/30 text-[#d4af37] text-[9px] font-bold uppercase px-2 py-1.5 rounded-sm hover:bg-[#d4af37] hover:text-black transition-all"
                                  >
                                    <ThumbsUp size={10} /> Loved
                                  </button>
                                  <button
                                    onClick={() => handleRate(result, false)}
                                    aria-label={`Mark ${result.title} as skipped`}
                                    className="flex items-center gap-1 border border-[#d4af37]/30 text-[#d4af37] text-[9px] font-bold uppercase px-2 py-1.5 rounded-sm hover:bg-[#4a0404] hover:border-red-500 hover:text-red-500 transition-all"
                                  >
                                    <ThumbsDown size={10} /> Skip
                                  </button>
                                </div>
                              )}
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  )}

                  {/* Empty state — after a query is typed but no results */}
                  {!isSearching && !error && query.trim().length >= 2 && results.length === 0 && (
                    <p className="text-xs text-white/40 font-serif-elegant italic text-center py-2">
                      The Critic found no matches for "{query.trim()}".
                    </p>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="flex-1 overflow-y-auto p-8">
          {history.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-white/30 space-y-4">
              <History size={64} opacity={0.2} />
              <p className="font-serif-elegant italic text-xl">Your film history is currently a blank reel.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {history.map((item, idx) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="flex items-center justify-between p-4 bg-black/40 rounded-lg border border-[#d4af37]/10 group hover:border-[#d4af37]/40 transition-all"
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-full ${item.liked ? 'bg-[#d4af37] text-black' : 'bg-white/5 text-[#d4af37]'}`}>
                      {item.liked ? <ThumbsUp size={16} /> : <ThumbsDown size={16} />}
                    </div>
                    <div>
                      <h3 className="font-bold text-white">{item.title}</h3>
                      <p className="text-[10px] text-white/40 uppercase tracking-widest">{item.liked ? 'Loved it' : 'Skipped'}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => onRemove(item.title)}
                    aria-label={`Remove ${item.title} from history`}
                    className="p-2 text-[#4a0404] bg-[#d4af37]/10 hover:bg-[#d4af37] hover:text-black rounded-sm transition-all opacity-0 group-hover:opacity-100"
                  >
                    <Trash2 size={16} />
                  </button>
                </motion.div>
              ))}
            </div>
          )}
        </div>
        
        <div className="p-6 bg-black/20 text-center">
          <p className="text-[10px] text-[#d4af37]/60 uppercase tracking-[0.2em]">History influences future recommendations</p>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default HistoryManager;
